import { t, useLanguage } from '../../i18n';
import LanguageSwitch from '../../components/LanguageSwitch';
import React, { useState, useContext } from 'react';
import { ThemePreferencesContext } from '../../contexts/ThemeContext';
import { View, StyleSheet, Image, KeyboardAvoidingView, Platform, ScrollView, TouchableOpacity, DeviceEventEmitter } from 'react-native';
import { TextInput, Button, Text, Snackbar, useTheme } from 'react-native-paper';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { auth } from '../../firebase';
import { Ionicons } from '@expo/vector-icons';

type LoginScreenProps = {
  navigation: any;
};

const LoginScreen = ({ navigation }: LoginScreenProps) => {
  useLanguage();
  const { toggleTheme, isDarkMode } = useContext(ThemePreferencesContext);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [visible, setVisible] = useState(false);
  const [secureTextEntry, setSecureTextEntry] = useState(true);

  const theme = useTheme();

  const handleLogin = async () => {
    if (!email || !password) {
      setError(t("メールアドレスとパスワードを入力してください"));
      setVisible(true);
      return;
    }

    try {
      setLoading(true);
      await signInWithEmailAndPassword(auth, email, password);
      // Navigation will be handled by the auth state listener in App.tsx
    } catch (error) {
      setError(t("ログインに失敗しました。メールアドレスとパスワードを確認してください"));
      setVisible(true);
    } finally {
      setLoading(false);
    }
  };

  const handleGuestLogin = () => {
    DeviceEventEmitter.emit('auth_guest_change', true);
  };

  return (
    <KeyboardAvoidingView
      style={[styles.container, { backgroundColor: theme.colors.background }]}
      behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
      keyboardVerticalOffset={Platform.OS === 'ios' ? 64 : 0}
    >
      <ScrollView contentContainerStyle={styles.scrollView}>
        <TouchableOpacity style={styles.themeToggle} onPress={toggleTheme}>
          <Ionicons name={isDarkMode ? 'sunny' : 'moon'} size={24} color={theme.colors.primary} />
        </TouchableOpacity>

        <View style={styles.logoContainer}>
          <Image
            source={require('../../assets/app_logo.png')}
            style={styles.logo}
            resizeMode="contain"
          />
          <Text style={[styles.title, { color: theme.colors.primary }]}>{t("TUFS時間割")}</Text>
        </View>

        <View style={styles.formContainer}>
          <LanguageSwitch />
          <TextInput
            label={t("メールアドレス")}
            value={email}
            onChangeText={setEmail}
            mode="outlined"
            style={styles.input}
            keyboardType="email-address"
            autoCapitalize="none"
            left={<TextInput.Icon icon={() => <Ionicons name="mail" size={20} color={theme.colors.onSurface} />} />}
          />

          <TextInput
            label={t("パスワード")}
            value={password}
            onChangeText={setPassword}
            mode="outlined"
            style={styles.input}
            secureTextEntry={secureTextEntry}
            right={
              <TextInput.Icon
                icon={() => <Ionicons name={secureTextEntry ? "eye" : "eye-off"} size={20} color={theme.colors.onSurface} />}
                onPress={() => setSecureTextEntry(!secureTextEntry)}
              />
            }
            left={<TextInput.Icon icon={() => <Ionicons name="lock-closed" size={20} color={theme.colors.onSurface} />} />}
          />

          <Button
            mode="contained"
            onPress={handleLogin}
            style={styles.button}
            loading={loading}
            disabled={loading}
          >
            {t("ログイン")}
          </Button>

          <Button
            mode="outlined"
            onPress={handleGuestLogin}
            style={[styles.button, { marginTop: 0 }]}
            disabled={loading}
          >
            {t("ログインせずに利用する")}
          </Button>

          <View style={styles.linksContainer}>
            <TouchableOpacity onPress={() => navigation.navigate('ForgotPassword')}>
              <Text style={[styles.link, { color: theme.colors.primary }]}>{t("パスワードを忘れた場合")}</Text>
            </TouchableOpacity>

            <View style={styles.registerContainer}>
              <Text style={{ color: theme.colors.onSurface }}>{t("アカウントをお持ちでない場合")} </Text>
              <TouchableOpacity onPress={() => navigation.navigate('Register')}>
                <Text style={[styles.link, { color: theme.colors.primary }]}>{t("登録")}</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>

        <Snackbar
          visible={visible}
          onDismiss={() => setVisible(false)}
          duration={3000}
          action={{
            label: t("閉じる"),
            onPress: () => setVisible(false),
          }}
        >
          {error}
        </Snackbar>
      </ScrollView>
    </KeyboardAvoidingView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  scrollView: {
    flexGrow: 1,
    justifyContent: 'center',
    padding: 20,
  },
  themeToggle: {
    position: 'absolute',
    top: 40,
    right: 20,
    padding: 10,
  },
  logoContainer: {
    alignItems: 'center',
    marginBottom: 40,
  },
  logo: {
    width: 100,
    height: 100,
    marginBottom: 10,
  },
  title: {
    fontSize: 24,
    fontWeight: 'bold',
  },
  formContainer: {
    width: '100%',
    maxWidth: 400,
    alignSelf: 'center',
  },
  input: {
    marginBottom: 16,
  },
  button: {
    marginTop: 8,
    marginBottom: 16,
    paddingVertical: 8,
  },
  linksContainer: {
    alignItems: 'center',
    marginTop: 16,
  },
  link: {
    fontWeight: 'bold',
  },
  registerContainer: {
    flexDirection: 'row',
    marginTop: 16,
  },
});

export default LoginScreen;
